import { BoxxGenericState, getGenericInitialState } from '@boxx/core';
import { Action, createReducer, on } from '@ngrx/store';
import {
  IEventsStateError,
  IEventsStateSuccess,
} from '../core/IStateErrorSuccess';
import { EventModel } from '../models/events.model';
import * as fromActions from './events.actions';

export interface EventsState
  extends BoxxGenericState<
    EventModel[],
    IEventsStateSuccess,
    IEventsStateError
  > {
  filteredItems: EventModel[];
  selectedId: number;
}

const initialState: EventsState = {
  ...getGenericInitialState(),
  data: [],
  filteredItems: [],
  selectedId: null,
};

const reducer = createReducer(
  initialState,
  // On Begin Actions
  on(
    fromActions.getEventsBeginAction,
    fromActions.createEventBeginAction,
    fromActions.updateEventBeginAction,
    fromActions.deleteEventBeginAction,
    (state): EventsState => ({
      ...state,
      error: null,
      success: null,
      isLoading: true,
    })
  ),

  // ON Fail Actions
  on(
    fromActions.getEventFailAction,
    fromActions.createEventFailAction,
    fromActions.updateEventFailAction,
    fromActions.deleteEventFailAction,
    (state, action): EventsState => ({
      ...state,
      isLoading: false,
      error: { after: getAfterActionType(action.type), error: action.errors },
    })
  ),

  // ON Success
  on(
    fromActions.getEventSuccessAction,
    (state, action): EventsState => ({
      ...state,
      isLoading: false,
      data: action.events,
      error: null,
      success: { after: getAfterActionType(action.type) },
      fetched: true,
    })
  ),
  on(
    fromActions.createEventSuccessAction,
    (state, action): EventsState => ({
      ...state,
      isLoading: false,
      data: [...action.payload],
      error: null,
      success: { after: getAfterActionType(action.type), data: action.eventId },
    })
  ),
  on(
    fromActions.updateEventSuccessAction,
    (state, action): EventsState => ({
      ...state,
      isLoading: false,
      data: [
        ...((el) => {
          const tmp = [...el];

          const idx = el.findIndex((m) => m.id === action.event.id);

          if (idx !== -1) {
            tmp.splice(idx, 1, action.event);
          }

          return tmp;
        })(state.data),
      ],
      error: null,
      success: { after: getAfterActionType(action.type) },
    })
  ),
  on(
    fromActions.deleteEventSuccessAction,
    (state, action): EventsState => ({
      ...state,
      isLoading: false,
      data: [...state.data.filter((m: EventModel) => +m.id !== action.eventId)],
      error: null,
      success: { after: getAfterActionType(action.type) },
    })
  ),

  // SELECT
  on(
    fromActions.selectEventAction,
    (state, action): EventsState => ({
      ...state,
      selectedId: action.eventId,
      error: null,
      success: null,
    })
  ),

  on(
    fromActions.clearEventsSuccessErrorDataAction,
    (state): EventsState => ({
      ...state,
      error: null,
      success: null,
    })
  )
);

const getAfterActionType = (type: fromActions.EventsActionTypes) => {
  let action: 'GET' | 'CREATE' | 'UPDATE' | 'DELETE' | 'UNKNOWN' = 'UNKNOWN';

  switch (type) {
    case fromActions.EventsActionTypes.getEventsSuccess:
    case fromActions.EventsActionTypes.getEventsFail:
      action = 'GET';
      break;
    case fromActions.EventsActionTypes.createEventSuccess:
    case fromActions.EventsActionTypes.createEventFail:
      action = 'CREATE';
      break;
    case fromActions.EventsActionTypes.updateEventSuccess:
    case fromActions.EventsActionTypes.updateEventFail:
      action = 'UPDATE';
      break;
    case fromActions.EventsActionTypes.deleteEventSuccess:
    case fromActions.EventsActionTypes.deleteEventFail:
      action = 'DELETE';
      break;
  }

  return action;
};

export const eventsReducer = (state: EventsState | undefined, action: Action) =>
  reducer(state, action);
